<template>
  <CRow>
    <CCol sm="12">
      <CCard class="entry-form-card">
        <CCardHeader>
          Zusätzliche Stunden für diesen Tag, nicht Abrechnungsfähig
        </CCardHeader>

        <CCardBody class="entry-form-body">
          <ValidationObserver ref="formObserver" v-slot="{ invalid }">
            <CForm @submit.prevent="newEntry">
              <div class="entry-form-section entry-form-section--subtle">
                <div class="entry-form-section__title">Rahmendaten</div>
                <div class="entry-form-meta">
                  <CInput v-model="localDatum" readonly="readonly" label="Datum">
                    <template #prepend-content><CIcon name="cil-calendar"/></template>
                  </CInput>

                  <CInput v-model="mitarbeiter" readonly="readonly" label="Mitarbeiter">
                    <template #prepend-content><CIcon name="cil-user"/></template>
                  </CInput>
                </div>
              </div>

              <div class="entry-form-section">
                <div class="entry-form-section__title">Auftrag</div>

                <div ref="kundeField" :class="fieldAnchorClass('kunde')">
                  <Firma v-model="kunde" form-label-class="d-block" form-label="Kunde*"/>
                </div>

                <div ref="projektField" :class="fieldAnchorClass('projekt')">
                  <Projekt v-model="projekt" form-label-class="d-block" form-label="Projekt bei Kunde" rules="" :kunde="kunde?kunde.kundenid:''"/>
                </div>
              </div>

              <div class="entry-form-section">
                <div class="entry-form-section__title">Erfassung</div>

                <div ref="arbeitszeitField" :class="fieldAnchorClass('arbeitszeit')">
                  <ArbeitszeitNumber rules="required" v-model="arbeitszeit" form-label="Arbeitszeit" />
                </div>

                <div ref="taetigkeitField" :class="fieldAnchorClass('taetigkeit')">
                  <TaetigkeitTextArea v-model="taetigkeit" form-label="Tätigkeit*" rows="3"/>
                </div>

                <TageskalenderBoolean v-model="zukuenftigBerechnen" form-label="Dem Kunden zukünfig berechnen?"/>
              </div>

              <div class="entry-form-section">
                <div class="entry-form-section__title">Anhänge</div>

                <div ref="bilderField" :class="fieldAnchorClass('bilder')">
                  <label class="entry-form-section__hint">Optionale Bilder</label>
                  <CRow class="imageupload mt-2">
                    <CCol sm="2"><TageskalenderImage v-model="image01" form-label-class="d-none"/></CCol>
                    <CCol sm="2"><TageskalenderImage v-model="image02" form-label-class="d-none"/></CCol>
                    <CCol sm="2"><TageskalenderImage v-model="image03" form-label-class="d-none"/></CCol>
                    <CCol sm="2"><TageskalenderImage v-model="image04" form-label-class="d-none"/></CCol>
                    <CCol sm="2"><TageskalenderImage v-model="image05" form-label-class="d-none"/></CCol>
                  </CRow>
                </div>
              </div>

              <div v-if="showSaveRetentionSelection" class="entry-form-section entry-form-section--retention">
                <SaveRetentionOptions
                  v-model="keepAfterSave"
                  :options="keepAfterSaveOptions"
                  :persist-value.sync="persistKeepAfterSave"
                />
              </div>

              <div class="mt-3">
                <FormValidationAssistant
                  :errors="validationErrors"
                  :visible="showValidationAssistant"
                  :show-summary="hasAttemptedSubmit"
                  @open="showValidationAssistant = true"
                  @close="showValidationAssistant = false"
                  @select="jumpToField"
                />
              </div>

              <CButton
                :color="btnColor(invalid)"
                class="btn-protokoll-submit entry-form-submit btn-block font-weight-bold pt-3 mt-4 pb-3"
                type="submit"
              >
                <CIcon name="cil-library-add"/>
                &nbsp;
                Dem Tageskalender Hinzufügen
              </CButton>
            </CForm>
          </ValidationObserver>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
import Vue from 'vue'
import moment from 'moment'
import Firma from '@/components/Firma'
import Projekt from '@/components/Projekt'
import FormValidationAssistant from '@/components/FormValidationAssistant'
import SaveRetentionOptions from '@/components/SaveRetentionOptions'
import ArbeitszeitNumber from '@/components/FormElements/Number'
import TaetigkeitTextArea from '@/components/FormElements/TextArea'
import TageskalenderBoolean from '@/components/FormElements/Boolean'
import TageskalenderImage from '@/components/FormElements/Image'

const KEEP_AFTER_SAVE_STORAGE_KEY = 'tageskalender-keep-after-save'
const KEEP_AFTER_SAVE_PERSISTENCE_KEY = 'tageskalender-keep-after-save-persist'
const SAVE_RETENTION_SELECTION_ENABLED = process.env.VUE_APP_ENABLE_SAVE_RETENTION_SELECTION !== 'false'

const createKeepAfterSaveDefaults = () => ({
  kunde: true,
  projekt: true,
  arbeitszeit: false,
  taetigkeit: false,
  zukuenftigBerechnen: false,
  image01: false,
  image02: false,
  image03: false,
  image04: false,
  image05: false
})

export default {
  name: 'tageskalender-formular',
  components: {
    Firma,
    Projekt,
    FormValidationAssistant,
    SaveRetentionOptions,
    ArbeitszeitNumber,
    TaetigkeitTextArea,
    TageskalenderBoolean,
    TageskalenderImage
  },
  props: {
    datum: [Date, String]
  },
  data () {
    return {
      kunde: null,
      projekt: null,
      arbeitszeit: null,
      taetigkeit: '',
      zukuenftigBerechnen: true,
      image01: null,
      image02: null,
      image03: null,
      image04: null,
      image05: null,
      mitarbeiter: '',
      keepAfterSave: createKeepAfterSaveDefaults(),
      persistKeepAfterSave: false,
      highlightedField: null,
      hasAttemptedSubmit: false,
      showValidationAssistant: false
    }
  },
  mounted () {
    this.loadKeepAfterSaveOptions()
    this.setInitialDefaults()
    this.mitarbeiterName()
  },
  computed: {
    user () {
      return this.$store.getters['security/user']
    },
    localDatum () {
      return moment(this.datum).format('DD.MM.YYYY')
    },
    keepAfterSaveOptions () {
      return [
        { key: 'kunde', label: 'Kunde' },
        { key: 'projekt', label: 'Projekt' },
        { key: 'arbeitszeit', label: 'Arbeitszeit' },
        { key: 'taetigkeit', label: 'Tätigkeit' },
        { key: 'zukuenftigBerechnen', label: 'Zukünftig berechnen' },
        { key: 'image01', label: 'Bild 1' },
        { key: 'image02', label: 'Bild 2' },
        { key: 'image03', label: 'Bild 3' },
        { key: 'image04', label: 'Bild 4' },
        { key: 'image05', label: 'Bild 5' }
      ]
    },
    showSaveRetentionSelection () {
      return SAVE_RETENTION_SELECTION_ENABLED
    },
    validationErrors () {
      const errors = []

      if (!this.kunde || !this.kunde.kundenid) {
        errors.push({
          id: 'kunde',
          label: 'Kunde auswählen',
          anchor: 'kunde',
          focusStrategy: 'button'
        })
      }

      if (this.arbeitszeit === '' || this.arbeitszeit === null || this.arbeitszeit === 0 || this.arbeitszeit === '0.00') {
        errors.push({
          id: 'arbeitszeit',
          label: 'Arbeitszeit ergänzen',
          anchor: 'arbeitszeit',
          focusStrategy: 'input'
        })
      }

      if (!this.taetigkeit || this.taetigkeit.trim().length === 0) {
        errors.push({
          id: 'taetigkeit',
          label: 'Tätigkeit ausfüllen',
          anchor: 'taetigkeit',
          focusStrategy: 'input'
        })
      }

      return errors
    }
  },
  watch: {
    keepAfterSave: {
      deep: true,
      handler: function () {
        if (this.persistKeepAfterSave) {
          localStorage.setItem(KEEP_AFTER_SAVE_STORAGE_KEY, JSON.stringify(this.keepAfterSave))
        }
      }
    },
    persistKeepAfterSave: function () {
      if (this.persistKeepAfterSave) {
        localStorage.setItem(KEEP_AFTER_SAVE_PERSISTENCE_KEY, 'true')
        localStorage.setItem(KEEP_AFTER_SAVE_STORAGE_KEY, JSON.stringify(this.keepAfterSave))
      } else {
        localStorage.removeItem(KEEP_AFTER_SAVE_PERSISTENCE_KEY)
        localStorage.removeItem(KEEP_AFTER_SAVE_STORAGE_KEY)
        this.keepAfterSave = createKeepAfterSaveDefaults()
      }
    }
  },
  methods: {
    async mitarbeiterName () {
      const mitarbeiterloaded = await this.loadMitarbeiter()
      const curUser = mitarbeiterloaded.data.filter(mitarbeiter => {
        return mitarbeiter.mitarbeiterid === this.user.userid
      })
      this.mitarbeiter = curUser.length > 0 ? curUser[0].name : ''
    },
    async loadMitarbeiter () {
      return Vue.axios.get('/mitarbeiter/get')
    },
    btnColor: function (observerInvalid = false) {
      if (!this.canSubmit(observerInvalid)) {
        return 'success'
      } else {
        return 'danger'
      }
    },
    canSubmit: function (observerInvalid = false) {
      return observerInvalid || this.validationErrors.length > 0
    },
    fieldAnchorClass (field) {
      return {
        'form-field-anchor': true,
        'form-field-anchor--active': this.highlightedField === field
      }
    },
    highlightField (field) {
      this.highlightedField = field

      window.clearTimeout(this.highlightTimeout)
      this.highlightTimeout = window.setTimeout(() => {
        if (this.highlightedField === field) {
          this.highlightedField = null
        }
      }, 1800)
    },
    findFocusableElement (container, focusStrategy = 'interactive') {
      if (!container) {
        return null
      }

      const selectors = {
        button: 'button:not([disabled])',
        input: 'input:not([disabled]), textarea:not([disabled]), select:not([disabled])',
        interactive: 'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      }

      return container.querySelector(selectors[focusStrategy] || selectors.interactive)
    },
    jumpToField (error) {
      const container = this.$refs[`${error.anchor}Field`]
      this.showValidationAssistant = false

      if (!container) {
        return
      }

      container.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
      this.highlightField(error.anchor)

      window.setTimeout(() => {
        const focusable = this.findFocusableElement(container, error.focusStrategy)
        if (focusable && focusable.focus) {
          focusable.focus({ preventScroll: true })
        }
      }, 220)
    },
    async collectValidationErrors () {
      if (this.$refs.formObserver) {
        await this.$refs.formObserver.validate()
      }

      return this.validationErrors
    },
    setInitialDefaults: function () {
      this.kunde = null
      this.projekt = null
      this.resetEntryFieldsToDefaults()
    },
    loadKeepAfterSaveOptions: function () {
      const defaults = createKeepAfterSaveDefaults()

      if (!SAVE_RETENTION_SELECTION_ENABLED) {
        this.persistKeepAfterSave = false
        this.keepAfterSave = defaults
        return
      }

      this.persistKeepAfterSave = localStorage.getItem(KEEP_AFTER_SAVE_PERSISTENCE_KEY) === 'true'

      if (!this.persistKeepAfterSave) {
        this.keepAfterSave = defaults
        return
      }

      const rawValue = localStorage.getItem(KEEP_AFTER_SAVE_STORAGE_KEY)

      if (!rawValue) {
        this.keepAfterSave = defaults
        return
      }

      try {
        this.keepAfterSave = {
          ...defaults,
          ...JSON.parse(rawValue)
        }
      } catch (error) {
        this.keepAfterSave = defaults
      }
    },
    resetEntryFieldsToDefaults: function () {
      this.arbeitszeit = null
      this.taetigkeit = ''
      this.zukuenftigBerechnen = true
      this.image01 = null
      this.image02 = null
      this.image03 = null
      this.image04 = null
      this.image05 = null
      this.highlightedField = null
      this.showValidationAssistant = false
      this.hasAttemptedSubmit = false
      this.$forceUpdate()
    },
    resetAfterSave: function () {
      if (!this.keepAfterSave.kunde) {
        this.kunde = null
      }

      if (!this.keepAfterSave.kunde || !this.keepAfterSave.projekt) {
        this.projekt = null
      }

      if (!this.keepAfterSave.arbeitszeit) {
        this.arbeitszeit = null
      }

      if (!this.keepAfterSave.taetigkeit) {
        this.taetigkeit = ''
      }

      if (!this.keepAfterSave.zukuenftigBerechnen) {
        this.zukuenftigBerechnen = true
      }

      if (!this.keepAfterSave.image01) {
        this.image01 = null
      }

      if (!this.keepAfterSave.image02) {
        this.image02 = null
      }

      if (!this.keepAfterSave.image03) {
        this.image03 = null
      }

      if (!this.keepAfterSave.image04) {
        this.image04 = null
      }

      if (!this.keepAfterSave.image05) {
        this.image05 = null
      }

      this.$forceUpdate()
    },
    async newEntry () {
      this.hasAttemptedSubmit = true

      const errors = await this.collectValidationErrors()
      if (errors.length > 0) {
        this.showValidationAssistant = true
        return
      }

      try {
        const response = await Vue.axios.post('/tageskalender/post', {
          datum: this.localDatum,
          mitarbeiter: this.user.userid,
          kunde: this.kunde.kundenid,
          projekt: this.projekt,
          dauer: this.arbeitszeit,
          taetigkeit: this.taetigkeit,
          zukuenftigBerechnen: this.zukuenftigBerechnen
        })

        await Promise.all([
          this.saveImage(response.data.tageskalenderid, 'bild01', this.image01),
          this.saveImage(response.data.tageskalenderid, 'bild02', this.image02),
          this.saveImage(response.data.tageskalenderid, 'bild03', this.image03),
          this.saveImage(response.data.tageskalenderid, 'bild04', this.image04),
          this.saveImage(response.data.tageskalenderid, 'bild05', this.image05)
        ])

        this.resetAfterSave()
        this.$snotify.success('Der Eintrag wurde angelegt, die Grunddaten werden neu ermittelt.', {
          position: 'rightTop',
          timeout: 4000
        })
        this.$eventBus.$emit('reloadtageskalender', [])
      } catch (error) {
        this.$snotify.error('Der Eintrag konnte nicht gespeichert werden.', {
          position: 'rightTop',
          timeout: 4000
        })
      }
    },
    saveImage (arbeitszeitid, selektor, image) {
      if (image) {
        const formData = new FormData()
        formData.append('selektor', selektor)
        formData.append('dbid', arbeitszeitid)
        formData.append('target', 'tageskalender')
        formData.append('uploadfile', image)

        return Vue.axios.post(
          '/bilder/upload',
          formData,
          {
            headers: {
              'content-type': `multipart/form-data; boundary=${formData._boundary}`
            }
          }
        )
      }

      return Promise.resolve()
    }
  }
}
</script>

<style lang="scss">
.entry-form-card {
  border-radius: 8px;
  box-shadow: none;
  overflow: hidden;
}

.entry-form-card > .card-header {
  background: #fff;
  border-bottom: 1px solid #d8dbe0;
  font-weight: 600;
  padding: 0.85rem 1rem;
}

.entry-form-body {
  background: #fff;
  padding: 1rem 1rem 0.9rem;
}

.entry-form-section {
  background: #fff;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
  margin-bottom: 0.9rem;
  padding: 0.9rem 1rem;
}

.entry-form-section--subtle {
  background: #f8f9fb;
}

.entry-form-section--retention {
  background: transparent;
  border: 0;
  padding: 0;
}

.entry-form-section__title {
  color: #1f2933;
  font-size: 0.92rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.entry-form-section__hint {
  color: #52606d;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.15rem;
}

.entry-form-meta .form-group:last-child {
  margin-bottom: 0;
}

.entry-form-submit {
  border-radius: 6px;
  box-shadow: none;
}

.form-field-anchor {
  border-radius: 6px;
  scroll-margin-top: 5rem;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
}

.form-field-anchor--active {
  background: #f3f8ff;
  box-shadow: 0 0 0 2px rgba(45, 114, 217, 0.24);
}

@media (max-width: 575.98px) {
  .entry-form-body {
    padding: 0.85rem 0.85rem 0.75rem;
  }

  .entry-form-section {
    border-radius: 8px;
    padding: 0.8rem 0.85rem;
    margin-bottom: 0.8rem;
  }
}
</style>
